import { LensTheme, PopoverContent } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  Button,
  List,
  ListItem,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { DrawerShell } from 'src/components/atlas/molecules/Drawer/DrawerShell';
import {
  editCaseTeamMemberAccess,
  filterPermissions,
  removeCaseTeamMember,
  reviewCaseTeamMemberModalVM,
} from 'src/components/organisms/Case/ReviewCaseTeamMemberModal/ReviewCaseTeamMemberModal.vm';
import { updateSelectedCaseTeamMemberPermissions } from 'src/components/pages/Case/Case.slice';
import {
  useEditCaseAccessMutation,
  useGetCaseTeamMemberPermissionsQuery,
  useRemoveFromCaseMutation,
} from 'src/graphql';
import { AppDispatch } from 'src/store';
import {
  CasePermissionsType,
  CaseTeamMemberPermissionsType,
  CaseTypeEnum,
} from 'src/types';
import { DateFormats, formatDate } from 'src/utils/time';
import { AccessStep } from './steps/AccessStep';

const arePermissionsEqual = (
  a: CasePermissionsType[],
  b: CasePermissionsType[],
) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

export const ReviewCaseTeamMemberDrawer = ({
  caseType,
  open,
  onClose,
  userId,
  caseId,
}: {
  caseType: CaseTypeEnum;
  open: boolean;
  onClose: () => void;
  userId: number | null;
  caseId: string;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { tokens, presets } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;
  const { AtlasListItemText } = presets.ListPresets.components;

  const { data: caseTeamMemberPermissions } =
    useGetCaseTeamMemberPermissionsQuery(
      {
        caseId,
        userId,
      },
      { enabled: open && userId != null },
    );

  useEffect(() => {
    dispatch(
      updateSelectedCaseTeamMemberPermissions(
        (caseTeamMemberPermissions?.getCaseTeamMemberPermissions ??
          []) as CasePermissionsType[],
      ),
    );
    return () => {
      dispatch(updateSelectedCaseTeamMemberPermissions([]));
    };
  }, [dispatch, caseTeamMemberPermissions]);

  const {
    name,
    site,
    role,
    country,
    status,
    invitedAt,
    filteredPermissions,
    canEditPermissions,
    isSuperAdmin,
    isReportAssignee,
  } = useSelector(reviewCaseTeamMemberModalVM(userId));

  const [editAccessMode, setEditAccessMode] = useState(false);
  const [permissionsState, setPermissionsState] =
    useState<CasePermissionsType[]>(filteredPermissions);

  useEffect(() => {
    if (editAccessMode) return;
    setPermissionsState(
      filterPermissions(
        (caseTeamMemberPermissions?.getCaseTeamMemberPermissions ??
          []) as CaseTeamMemberPermissionsType[],
      ) as CasePermissionsType[],
    );
  }, [caseTeamMemberPermissions?.getCaseTeamMemberPermissions, editAccessMode]);

  // The drawer stays mounted while closed, so discard any in-progress edits
  // when it closes to ensure it reopens in read-only mode.
  useEffect(() => {
    if (!open) {
      setEditAccessMode(false);
      setPermissionsState(filteredPermissions);
    }
  }, [open, filteredPermissions]);

  const handleEditPermissions = (
    updatedPermissions: CasePermissionsType[],
    action: 'add' | 'remove',
  ) => {
    if (action === 'add') {
      setPermissionsState((prevState) => [
        ...new Set([...prevState, ...updatedPermissions]),
      ]);
      return;
    }

    if (isSuperAdmin && updatedPermissions.includes('SEND_REPORT_MESSAGES')) {
      // Super Admins must always keep the `VIEW_REPORT_MESSAGES` permission
      setPermissionsState((prevState) => [
        ...prevState.filter(
          (permission) => !updatedPermissions.includes(permission),
        ),
        'VIEW_REPORT_MESSAGES',
      ]);
    } else if (
      // Report Assignees must always keep `VIEW_CASE_DETAILS` (and
      // `VIEW_CASE_DETAILS_PII` if it was checked)
      isReportAssignee &&
      updatedPermissions.includes('EDIT_CASE_DETAILS')
    ) {
      const piiPermission = permissionsState.includes('VIEW_CASE_DETAILS_PII');
      setPermissionsState((prevState) => [
        ...prevState.filter(
          (permission) => !updatedPermissions.includes(permission),
        ),
        ...(piiPermission
          ? (['VIEW_CASE_DETAILS_PII'] as CasePermissionsType[])
          : []),
        'VIEW_CASE_DETAILS',
      ]);
    } else {
      setPermissionsState((prevState) =>
        prevState.filter(
          (permission) => !updatedPermissions.includes(permission),
        ),
      );
    }
  };

  const userProps = [
    {
      label: 'Name',
      value: name,
    },
    {
      label: 'Role',
      value: role,
    },
    {
      label: 'Permissions',
      value: isSuperAdmin ? 'Super Admin' : 'Team Member',
    },
    {
      label: 'Site',
      value: site,
    },
    {
      label: 'Country',
      value: country,
    },
    {
      label: 'Status',
      value: status,
    },
    {
      label: 'Invited to case',
      value: formatDate(invitedAt, DateFormats.DD_MM_YYYY),
    },
  ];

  const queryClient = useQueryClient();

  const { mutateAsync: removeFromCase } = useRemoveFromCaseMutation();
  const { mutateAsync: editCaseAccess } = useEditCaseAccessMutation();

  const [anchorElRemoveConfirmation, setAnchorElRemoveConfirmation] =
    useState<HTMLButtonElement | null>(null);
  const [anchorElSaveConfirmation, setAnchorElSaveConfirmation] =
    useState<HTMLButtonElement | null>(null);

  const handleRemoveConfirmationPopoverOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorElRemoveConfirmation(event.currentTarget);
  };

  const handleRemoveConfirmationPopoverClose = () => {
    setAnchorElRemoveConfirmation(null);
  };

  const handleSaveConfirmationPopoverClose = () => {
    setAnchorElSaveConfirmation(null);
  };

  const hasUnsavedChanges = !arePermissionsEqual(
    filteredPermissions,
    permissionsState,
  );

  const handlePrimaryAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!editAccessMode) {
      setEditAccessMode(true);
      return;
    }
    setAnchorElSaveConfirmation(event.currentTarget);
  };

  const handleCancelEdit = () => {
    setPermissionsState(filteredPermissions);
    setEditAccessMode(false);
  };

  const handleConfirmSave = () => {
    if (userId) {
      dispatch(
        editCaseTeamMemberAccess({
          caseId,
          userId,
          entityPermissions: permissionsState,
          mutationEditCaseAccess: editCaseAccess,
          queryClient,
        }),
      );
    }
    handleSaveConfirmationPopoverClose();
    setEditAccessMode(false);
    onClose();
  };

  return (
    <>
      <DrawerShell
        open={open}
        title={editAccessMode ? 'Edit access' : `Reviewer: ${name}`}
        onRequestClose={onClose}
        nextTitle={editAccessMode ? 'Save changes' : 'Edit access'}
        onNext={handlePrimaryAction}
        nextDisabled={
          !canEditPermissions || (editAccessMode && !hasUnsavedChanges)
        }
        cancelTitle={editAccessMode ? 'Cancel' : 'Remove'}
        onCustomCancel={
          editAccessMode
            ? handleCancelEdit
            : (event) => handleRemoveConfirmationPopoverOpen(event)
        }
        cancelButtonState={editAccessMode ? 'default' : 'danger'}
        cancelDisabled={!canEditPermissions}
        testId="review-team-drawer"
      >
        <Box
          sx={{
            display: 'flex',
            borderRadius: tokens.semantic.radius.lg.value,
            background: tokens.semantic.color.surface.variant.value,
            gap: sp[3].value,
            padding: sp[3].value,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRadius: tokens.semantic.radius.lg.value,
              background: tokens.semantic.color.background.base.value,
              padding: sp[2].value,
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="h3"
              sx={{ marginBottom: sp[2].value, fontWeight: 700 }}
            >
              Profile
            </Typography>
            <List>
              {userProps.map(({ label, value }, index) => (
                <ListItem key={index} dense>
                  <AtlasListItemText
                    primary={label}
                    secondary={value}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: sp[1].value,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
        <AccessStep
          permissions={editAccessMode ? permissionsState : filteredPermissions}
          handleSetPermissions={handleEditPermissions}
          selectedSuperAdmins={
            isSuperAdmin && userId ? [{ label: name, value: name }] : []
          }
          selectedApprovers={[]}
          handleDeleteUserChip={() => {}}
          caseType={caseType}
          isReadOnly={!editAccessMode}
        />
      </DrawerShell>
      <Popover
        id={'remove-team-member-popover'}
        open={Boolean(anchorElRemoveConfirmation)}
        anchorEl={anchorElRemoveConfirmation}
        onClose={handleRemoveConfirmationPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <PopoverContent
          title="Are you sure you want to remove this team member?"
          onClose={handleRemoveConfirmationPopoverClose}
          primaryAction={
            <Button
              variant="contained"
              onClick={() => {
                if (userId) {
                  dispatch(
                    removeCaseTeamMember({
                      caseId,
                      userId,
                      mutationRemoveCaseTeamMember: removeFromCase,
                      queryClient,
                    }),
                  );
                  handleRemoveConfirmationPopoverClose();
                  onClose();
                }
              }}
            >
              Confirm
            </Button>
          }
          secondaryAction={
            <Button
              variant="text"
              onClick={handleRemoveConfirmationPopoverClose}
            >
              Cancel
            </Button>
          }
        >
          <Typography variant="body1">
            Before you proceed, please consider the implications of removing a
            team member from this case. This action cannot be undone and will
            revoke their access to the case&apos;s information and collaboration
            tools.
            <br />
            Removing a team member may impact the progress and efficiency of
            your case. Make sure to communicate any changes within your team to
            ensure seamless workflow.
          </Typography>
        </PopoverContent>
      </Popover>
      <Popover
        id={'save-team-member-access-popover'}
        open={Boolean(anchorElSaveConfirmation)}
        anchorEl={anchorElSaveConfirmation}
        onClose={handleSaveConfirmationPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <PopoverContent
          title="Confirm changes to case access"
          onClose={handleSaveConfirmationPopoverClose}
          primaryAction={
            <Button
              variant="contained"
              onClick={handleConfirmSave}
              data-testid="confirm-save-access"
            >
              Confirm changes
            </Button>
          }
          secondaryAction={
            <Button variant="text" onClick={handleSaveConfirmationPopoverClose}>
              Cancel
            </Button>
          }
        >
          <Typography variant="body1">
            Are you sure you want to edit the level of access for this team
            member? Please ensure that this adjustment aligns with your intended
            changes before proceeding.
          </Typography>
        </PopoverContent>
      </Popover>
    </>
  );
};
